import { computed, ref } from 'vue'

import {
  CopyrightOwnerType,
  CopyrightOwnersDocument,
  CopyrightOwnersNewDocument,
  useUpdateCopyrightOwnerMutation,
  type CopyrightOwnerNew,
  type CopyrightOwnersQuery,
  type CopyrightOwnersNewQuery,
} from '@/shared/graphql/legacy/generated'

export function useEditCopyrightOwner(owner: CopyrightOwnerNew) {
  const {
    loading,
    mutate: updateCopyrightOwner,
    error,
  } = useUpdateCopyrightOwnerMutation()

  const name = ref(owner.name)

  const isOrganization = owner.type === CopyrightOwnerType.Organization

  const title = computed(() =>
    isOrganization ? 'Edit organization' : 'Edit copyright owner'
  )

  const label = computed(() => (isOrganization ? 'Organization name' : 'Name'))

  const canSave = computed(
    () => !!name.value.trim() && name.value.trim() !== owner.name.trim()
  )

  const save = async () => {
    if (!canSave.value) return false

    await updateCopyrightOwner(
      { id: owner.id, name: name.value },
      {
        update(cache, { data }) {
          if (!data) return

          cache.updateQuery<CopyrightOwnersNewQuery>(
            { query: CopyrightOwnersNewDocument },
            (response) => {
              if (!response) return

              return {
                ...response,
                copyrightOwnersNew: {
                  ...response.copyrightOwnersNew,
                  items: response.copyrightOwnersNew.items.map((item) =>
                    item.id === owner.id ? { ...item, name: name.value } : item
                  ),
                },
              }
            }
          )

          cache.updateQuery<CopyrightOwnersQuery>(
            { query: CopyrightOwnersDocument },
            (response) => {
              if (!response) return

              return {
                ...response,
                copyrightOwners: {
                  ...response.copyrightOwners,
                  items: response.copyrightOwners.items.map((item) =>
                    item.id === owner.id ? { ...item, name: name.value } : item
                  ),
                },
              }
            }
          )
        },
      }
    )

    return true
  }

  return { name, title, label, loading, error, canSave, save }
}
